import React, { useContext } from 'react';
import { DrawerOverlay } from '@99designs/apps/spa/common/DrawerOverlay/DrawerOverlay';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import Visible from '@99designs/apps/spa/pages/create/common/components/Visible';
import Confetti from '@99designs/apps/spa/pages/create/components/Confetti/Confetti';
import { redirectLaunchedBrief } from '@99designs/apps/spa/pages/create/util/routes';
import { Box, Heading, Paragraph } from '@99designs/emu';
import { Button } from '@99designs/frontend-components/components/Button';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { FormattedHTMLMessage } from '@99designs/frontend-components/i18n';
import { IProduct } from '@99designs/graph-utils/types';
import { linkTo } from '@99designs/routing';
import { Partner, PartnerContext } from '@99designs/spa-common';

export enum LaunchState {
    Success = 'Success',
    Error = 'Error',
}

interface Brief {
    designers: { displayName: string }[];
    projectIds: number[];
    package?: IProduct;
}

interface Props {
    state: LaunchState;
    brief: Brief;
    showing: boolean;
}

const ModalBackgroundEffect = (state: LaunchState) => {
    const { partner } = useContext(PartnerContext);

    // eslint-disable-next-line eqeqeq
    if (state == LaunchState.Success && partner == Partner.Facebook) {
        return <Confetti />;
    }
};

const LaunchMessage = ({ brief }: { brief: Brief }) => {
    // eslint-disable-next-line eqeqeq
    const isSingleDesigner = brief.designers.length == 1;

    if (brief.package && isSingleDesigner) {
        return (
            <>
                <Heading accessibilityLevel={3} withDivider>
                    <FormattedHTMLMessage
                        message={__('Done!<br />We’ve sent your project to {displayName}.')}
                        values={{ displayName: brief.designers[0].displayName }}
                    />
                </Heading>
                <Paragraph>{__('You’ll receive a response within 24 hours.')}</Paragraph>
            </>
        );
    }

    if (!brief.package) {
        return (
            <>
                <Heading accessibilityLevel={3} withDivider>
                    {isSingleDesigner ? (
                        <FormattedHTMLMessage
                            message={__('Done!<br />We’ve sent your invite to {displayName}.')}
                            values={{ displayName: brief.designers[0].displayName }}
                        />
                    ) : (
                        <FormattedHTMLMessage
                            message={__(
                                'Done!<br />We’ve sent out {number} invites on your behalf'
                            )}
                            values={{
                                number: brief.projectIds.length,
                            }}
                        />
                    )}
                </Heading>

                <Paragraph>
                    {isSingleDesigner ? (
                        __('You’ll receive a response and/or quote within 24 hours.')
                    ) : (
                        <FormattedHTMLMessage
                            message={__(
                                'You’ll start to receive responses and/or quotes from designers within 24 hours. You can keep an eye on progress from inside of <strong>Your Work.</strong>'
                            )}
                        />
                    )}
                </Paragraph>
            </>
        );
    }

    // all other briefs will launch as jobs
    return (
        <>
            <Heading accessibilityLevel={3} withDivider>
                <FormattedHTMLMessage message={__('Done!<br />Your project has been posted.')} />
            </Heading>
            <Paragraph>
                {__(
                    'Designers will let you know if they are interested. Then you can choose a designer to work with, and officially launch your project.'
                )}
            </Paragraph>
        </>
    );
};

const LaunchModal = ({ state, brief, showing }: Props) => {
    const background = ModalBackgroundEffect(state);

    return (
        <DrawerOverlay showing={showing} variations={['center']} background={background}>
            {/* eslint-disable-next-line eqeqeq */}
            {state == LaunchState.Success && (
                <>
                    <Box color={'forrestLyf'}>
                        <Icon type={'tick-3'} variations={['4x']} />
                    </Box>
                    <LaunchMessage brief={brief} />

                    <Box color="grey800" marginTop="1bu">
                        <Visible sizes={['small', 'medium']}>
                            <Button
                                onClick={() => redirectLaunchedBrief(brief)}
                                variations={['primary', 'chameleon']}
                            >
                                {__('Continue')}
                            </Button>
                        </Visible>
                        <Visible sizes={['normal', 'large']}>
                            <Button
                                onClick={() => redirectLaunchedBrief(brief)}
                                variations={['primary', 'jumbo', 'chameleon']}
                                data-testid="continue-button-large-screen"
                            >
                                {__('Continue')}
                            </Button>
                        </Visible>
                    </Box>
                </>
            )}
            {/* eslint-disable-next-line eqeqeq */}
            {state == LaunchState.Error && (
                <>
                    <Box color={'risingSun'}>
                        <Icon type={'exclamation-point-2'} variations={['4x']} />
                    </Box>
                    <div className="heading heading--with-divider heading--h3">
                        <FormattedHTMLMessage
                            message={__('Uh oh!<br />Something has gone wrong')}
                        />
                    </div>
                    <Paragraph>
                        {__(
                            'Please try again in a couple of minutes or reach out to our friendly Support Team for help.'
                        )}
                    </Paragraph>
                    <Box color={'risingSun'} marginTop="1bu">
                        <Visible sizes={['small', 'medium']}>
                            <Button
                                onClick={() => redirectTo(linkTo.help())}
                                variations={['primary', 'chameleon']}
                            >
                                {__('Contact support')}
                            </Button>
                        </Visible>
                        <Visible sizes={['normal', 'large']}>
                            <Button
                                onClick={() => redirectTo(linkTo.help())}
                                variations={['primary', 'jumbo', 'chameleon']}
                            >
                                {__('Contact support')}
                            </Button>
                        </Visible>
                    </Box>
                </>
            )}
        </DrawerOverlay>
    );
};

export default LaunchModal;
